<template>
  <div class="customize-video">
    <div 
      v-if="playerConfig" 
      id="myvideo" 
      ref="videoContainer"
      class="customize-video__player-host"
      :style="playerHostStyle"
    ></div>
    <!-- 直播未开始：match.poster 背景 + 蒙层 + 预计开始时间（beginDate） -->
    <div
      v-else-if="showStartTime"
      class="countdown-wrap"
      :style="{ height: videoHeight > 0 ? videoHeight + 'px' : '200px' }"
    >
      <div 
        class="countdown-bg" 
        :style="{ backgroundImage: posterBg }"
      ></div>
      <div class="countdown-mask"></div>
      <div class="countdown-box">
        预计<span class="countdown-num">{{ beginDateFormatted }}</span>开始
      </div>
    </div>
    <div v-else class="no-video">{{ $t('livePage.noVideo') }}</div>
  </div>
</template>

<script>
import { QNImageUtil } from '@/utils'
import Moment from 'moment'

export default {
    props: {
        // 播放器配置对象
        playerConfig: {
            type: Object,
            default: null
        },
        // 视频高度（根据16:9比例计算）
        videoHeight: {
            type: Number,
            default: 0
        },
        // 赛事信息（含 poster，未开始直播时作背景）
        match: {
            type: Object,
            default: () => ({})
        },
        // 比赛信息（含 beginDate，未开始直播时展示预计开始时间）
        game: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            player: null,
            /** 用于在全屏结束后强制与 videoHeight 同步，避免 xgplayer 残留 100vh 等样式 */
            hostHeightPx: null
        };
    },
    computed: {
        playerHostStyle() {
            const h = this.hostHeightPx != null ? this.hostHeightPx : this.videoHeight
            if (h > 0) {
                return {
                    width: '100%',
                    height: `${h}px`,
                    minHeight: '0',
                    maxHeight: 'none'
                }
            }
            return { width: '100%', height: '100%', minHeight: '0' }
        },
        // 是否显示「未开始直播」预计开始时间：无播放器且 game.beginDate 存在
        showStartTime() {
            return !this.playerConfig && this.game && this.game.beginDate
        },
        // 背景图：match.poster，无则无图（靠蒙层+底色）
        posterBg() {
            const poster = this.match && this.match.poster
            if (!poster) return 'none'
            const url = QNImageUtil.imageView(poster, 1080, 608)
            return url ? `url(${url})` : 'none'
        },
        // 预计开始时间格式化（beginDate，直播可能提前或延迟，仅展示计划时间）
        beginDateFormatted() {
            if (!this.game || !this.game.beginDate) return ''
            return Moment(this.game.beginDate).format('M月D日 HH:mm')
        }
    },
    watch: {
        videoHeight: {
            handler(h) {
                if (h > 0) this.hostHeightPx = h
            },
            immediate: true
        },
        // 监听配置对象变化，重新初始化播放器
        playerConfig: {
            handler(newConfig) {
                if (newConfig) {
                    this.$nextTick(() => {
                        this.initPlayer(newConfig)
                    })
                }
            },
            immediate: true
        }
    },
    mounted() {
        this._onFullscreenChange = () => {
            this.$nextTick(() => {
                if (!this.getFullscreenElement()) {
                    this.fixLayoutAfterFullscreen()
                }
            })
        }
        document.addEventListener('fullscreenchange', this._onFullscreenChange)
        document.addEventListener('webkitfullscreenchange', this._onFullscreenChange)
        document.addEventListener('mozfullscreenchange', this._onFullscreenChange)
        document.addEventListener('MSFullscreenChange', this._onFullscreenChange)
        this._onWinResize = this.debounce(() => {
            this.fixLayoutAfterFullscreen()
        }, 200)
        window.addEventListener('resize', this._onWinResize)
        window.addEventListener('orientationchange', this._onWinResize)
    },
    beforeDestroy() {
        if (this._onFullscreenChange) {
            document.removeEventListener('fullscreenchange', this._onFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange)
            document.removeEventListener('mozfullscreenchange', this._onFullscreenChange)
            document.removeEventListener('MSFullscreenChange', this._onFullscreenChange)
        }
        if (this._onWinResize) {
            window.removeEventListener('resize', this._onWinResize)
            window.removeEventListener('orientationchange', this._onWinResize)
        }
        this.detachVideoFullscreenListeners()
        this.destroyPlayer()
    },
    methods: {
        getFullscreenElement() {
            return (
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            )
        },
        debounce(fn, ms) {
            let t
            return (...args) => {
                clearTimeout(t)
                t = setTimeout(() => fn(...args), ms)
            }
        },
        /**
         * 全屏退出 / 转屏后：收回播放器对容器写入的异常高度，并触发 xgplayer resize
         */
        fixLayoutAfterFullscreen() {
            const h = Number(this.videoHeight) || 0
            if (h > 0) this.hostHeightPx = h
            this.$nextTick(() => {
                const wrap = this.$refs.videoContainer
                if (wrap && h > 0) {
                    wrap.style.height = `${h}px`
                    wrap.style.width = '100%'
                    wrap.style.minHeight = '0'
                    wrap.style.maxHeight = 'none'
                    const root = wrap.querySelector('.xgplayer')
                    if (root) {
                        root.style.width = '100%'
                        root.style.height = `${h}px`
                        root.style.maxHeight = 'none'
                    }
                }
                if (this.player) {
                    try {
                        if (typeof this.player.resize === 'function') {
                            this.player.resize()
                        }
                    } catch (e) {
                        /* noop */
                    }
                }
                this.$emit('layout-fixed')
            })
        },
        attachVideoFullscreenListeners() {
            this.detachVideoFullscreenListeners()
            const wrap = this.$refs.videoContainer
            const videoEl = wrap && wrap.querySelector('video')
            if (!videoEl) return
            this._onWebkitEndFullscreen = () => {
                this.fixLayoutAfterFullscreen()
            }
            videoEl.addEventListener('webkitendfullscreen', this._onWebkitEndFullscreen)
            this._videoFsEl = videoEl
        },
        detachVideoFullscreenListeners() {
            if (this._videoFsEl && this._onWebkitEndFullscreen) {
                this._videoFsEl.removeEventListener('webkitendfullscreen', this._onWebkitEndFullscreen)
            }
            this._videoFsEl = null
            this._onWebkitEndFullscreen = null
        },
        /**
         * 初始化播放器
         * @param {Object} config - 播放器配置对象
         */
        initPlayer(config) {
            try {
                // 如果已存在播放器，先销毁
                this.destroyPlayer()

                // 使用 xgplayer 初始化播放器
                if (window.Player) {
                    this.player = new window.Player(config)
                    console.log('播放器初始化成功', this.player)
                    this.$nextTick(() => {
                        this.fixLayoutAfterFullscreen()
                        this.attachVideoFullscreenListeners()
                    })
                } else {
                    console.error('xgplayer 未加载，请确保已引入 xgplayer')
                }
            } catch (error) {
                console.error('播放器初始化失败:', error)
            }
        },
        /**
         * 销毁播放器
         */
        destroyPlayer() {
            this.detachVideoFullscreenListeners()
            if (this.player) {
                try {
                    this.player.destroy()
                    this.player = null
                } catch (error) {
                    console.error('播放器销毁失败:', error)
                }
            }
        },
    },
};
</script>

<style scoped lang="scss">
.customize-video {
  width: 100%;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  .customize-video__player-host,
  #myvideo {
    width: 100%;
    max-width: 100%;
    min-height: 0;
    background: #000;
    box-sizing: border-box;
  }
  
  .no-video {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 200px;
    color: #999;
    font-size: 14px;
  }

  /* 直播未开始：背景 + 蒙层 + 预计开始时间 */
  .countdown-wrap {
    position: relative;
    width: 100%;
    overflow: hidden;
  }
  .countdown-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-color: #2a2a2a;
    filter: blur(6px);
    transform: scale(1.08);
  }
  .countdown-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
  }
  .countdown-box {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    // padding: 14px 24px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.4);
    color: #fff;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 37px;
    padding: 0 26px;
    border-radius: 60px;
    width: 200px;
  }
  .countdown-num {
    color: #D54F3F;
    font-size: 16px;
    margin: 0 4px;
  }
}
</style>
